import React, { FC, useMemo, useState } from 'react'

import { Visibility, Delete } from '@mui/icons-material'
import { Box, Typography, Tab, Tabs } from '@mui/material'
import { LinearProgress } from '@mui/material'
import { DataGrid, GridColDef, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid'

import { formatFileSize } from 'utils/formatFileSize'
import { showNotification } from 'utils/showNotification'

import { useActions } from 'hooks/useActions'

import {
  useGetEmailByIdQuery,
  useGetStatisticChartsQuery,
  useGetStatisticFrequencyTablesQuery,
  useMarkAsSpamMutation,
} from 'services/dashboard/api'

import ConfirmModal from 'components/common/ConfirmModal/index'
import StatisticCharts from 'components/mails/mail-stats/StatisticCharts'
import { ShowMessageNewModal } from 'components/mails/mails/ShowMessageNewModal'
import NoDataInDataGridTable from 'components/table/NoDataInTable'
import { formatToLocaleTimezone } from 'utils/formatToLocalTimezone'

interface FrequencyRecord {
  count: number
  spamPercentage: number
  sender: string
  subject: string
  averageMailSize: number
  summaryDiskUsage: number
  sampleMailId: string | null
}

export type MailTabActions = 'SHOW_MESSAGE' | 'DELETE' | ''

export const MailStats: FC = () => {
  const [tabValue, setTabValue] = useState<'All Time' | 'Month' | 'Day'>('All Time')
  const { modal, setModal, singleRow, setSingleRow } = useActions<MailTabActions>()
  const { data: statisticFrequencyTables, isFetching } = useGetStatisticFrequencyTablesQuery(
    undefined,
    {
      pollingInterval: 5 * 60 * 1000,
    }
  )
  const { data: emailData } = useGetEmailByIdQuery(
    { mailId: singleRow?.sampleMailId },
    {
      skip: !singleRow || modal !== 'SHOW_MESSAGE',
      selectFromResult: ({ data, isFetching }) => ({
        data: isFetching ? undefined : data,
        isFetching,
      }),
    }
  )

  const [markAsSpam, { isLoading: isLoadingMarkAsSpam }] = useMarkAsSpamMutation()
  const { data: statisticCharts, isFetching: isFetchingCharts } = useGetStatisticChartsQuery()
  const handleTabChange = (event: React.SyntheticEvent, newValue: 'All Time' | 'Month' | 'Day') => {
    setTabValue(newValue)
  }

  const handleDeleteMail = ({ sender, subject }: { sender: string; subject: string }) => {
    markAsSpam({ sender, subject })
      .unwrap()
      .then(() => {
        showNotification('Mail deleted', 'success')
        setModal('')
        setSingleRow(null)
      })
      .catch(() => {
        showNotification('Error. Try again later', 'error')
        setModal('')
        setSingleRow(null)
      })
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'sender',
        headerName: 'Sender',
        minWidth: 150,
        flex: 2,
      },
      {
        field: 'subject',
        headerName: 'Subject',
        minWidth: 200,
        flex: 3,
      },
      {
        field: 'count',
        headerName: 'Count',
        minWidth: 100,
        flex: 0.7,
      },

      {
        field: 'spamPercentage',
        headerName: 'Spam',
        minWidth: 100,
        valueFormatter: (params) => {
          const percentage = params.value * 100;
          return percentage % 1 === 0 ? `${percentage}%` : `${percentage.toFixed(2)}%`;
        },
        flex: 1,
      },
      {
        field: 'averageMailSize',
        headerName: 'Avg Mail Size',
        minWidth: 120,
        flex: 1,
        valueFormatter: (params) => formatFileSize(params.value),
        renderCell: (params) => formatFileSize(params.value),
      },
      {
        field: 'summaryDiskUsage',
        headerName: 'Disk Usage',
        minWidth: 120,
        flex: 1,
        valueFormatter: (params) => formatFileSize(params.value),
        renderCell: (params) => formatFileSize(params.value),
      },
      {
        field: 'firstMailDate',
        headerName: 'First Mail Date',
        minWidth: 120,
        flex: 1.5,
        type: 'Datetime',
        valueFormatter: ({ value }) => formatToLocaleTimezone(value),
      },
      {
        field: 'lastMailDate',
        headerName: 'Last Mail Date',
        minWidth: 120,
        flex: 1.5,
        type: 'Datetime',
        valueFormatter: ({ value }) => formatToLocaleTimezone(value),
      },
      {
        field: 'actions',
        type: 'actions',
        width: 120,
        getActions: (params: GridRowParams<FrequencyRecord>) => [
          <GridActionsCellItem
            key="Show Message"
            icon={<Visibility />}
            label="Show Message"
            onClick={() => {
              setModal('SHOW_MESSAGE')
              setSingleRow(params.row)
            }}
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => {
              setModal('DELETE')
              setSingleRow(params.row)
            }}
            showInMenu
          />,
        ],
      },
    ],
    []
  )

  const getCurrentTableData = () => {
    if (!statisticFrequencyTables) return []

    switch (tabValue) {
      case 'Day':
        return statisticFrequencyTables.dayTable.data
      case 'Month':
        return statisticFrequencyTables.monthTable.data
      case 'All Time':
        return statisticFrequencyTables.allTimeTable.data
      default:
        return []
    }
  }

  if (isFetchingCharts) {
    return <LinearProgress sx={{ mt: '8px' }} />
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '4px',
        minHeight: 'calc(100vh - 130px)',
        mb:'30px'
      }}
    >
      <StatisticCharts data={statisticCharts} isLoading={isFetchingCharts}/>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Mails Frequency
        </Typography>
        <Box sx={{ p: 1 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="mail stats tabs">
            <Tab label="All Time" value={'All Time'} />
            <Tab label="Day" value={'Day'} />
            <Tab label="Month" value={'Month'} />
          </Tabs>
        </Box>

        <Box>
          <DataGrid
            rows={getCurrentTableData()}
            columns={columns}
            loading={isFetching}
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            getRowId={(row) => `${row.sampleMailId}`}
            slots={{
              loadingOverlay: LinearProgress,
              noRowsOverlay: NoDataInDataGridTable,
            }}
            sx={{
              '& .MuiDataGrid-virtualScrollerContent': {
                minHeight: '200px !important',
              },
              ...((!isFetching || (isFetching && getCurrentTableData().length !== 0)) && {
                borderBottom: 'none',
              }),
            }}
          />
        </Box>
      </Box>
      {modal === 'SHOW_MESSAGE' && (
        <ShowMessageNewModal
          onClose={() => setModal('')}
          mail={{
            id: singleRow.sampleMailId,
            sender: emailData?.sender || '',
            recipient: emailData?.recipient || '',
            subject: emailData?.subject || '',
            receivedAt: emailData?.receivedAt || '',
            savedAt: emailData?.savedAt || '',
            trustRating: emailData?.trustRating || 0,
          }}
        />
      )}
      {modal === 'DELETE' && (
        <ConfirmModal
          onConfirm={() => handleDeleteMail(singleRow)}
          onClose={() => {
            setModal('')
            setSingleRow(null)
          }}
          open={true}
          loading={isLoadingMarkAsSpam}
        >
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            Are you sure you want to delete this mail?
          </Typography>
        </ConfirmModal>
      )}
    </Box>
  )
}
